<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            ajax-path="wideband/tickets"
            :ajax-params="ajaxParams"            
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            @rowSelectionChanged="preview"
        />
        <app-modal 
            width="lg:w-2/3"
            bg="bg-gray-200"
            :show.sync="showPreviewModal"
            @close="showPreviewModal = false"
        >
            <template slot="header">Trouble Ticket Details</template>
            <template slot="body">
                <div class="card">
                    <table class="table">
                        <tr>
                            <td width="200"><strong>Wideband Ticket ID</strong></td>
                            <td>
                                <div>{{ ticket.id }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Created At</strong></td>
                            <td>
                                <div>{{ ticket.created_at }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Updated At</strong></td>
                            <td>
                                <div>{{ ticket.updated_at }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Severity</strong></td>
                            <td>
                                <div>{{ ticket.severity }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Status</strong></td>
                            <td>
                                <div>
                                    {{ ticket.status }}<br>
                                    <span class="text-xs text-gray-600">{{ ticket.last_notification_type }}</span>
                                </div>
                            </td>                            
                        </tr>
                        <tr v-if="ticket.last_notification_type == 'IncidentResolved'">
                            <td><strong>Your Response</strong></td>
                            <td>
                                <table class="table">
                                    <tr>
                                        <td>
                                            <radio
                                                v-model="clearanceForm.clearanceType"
                                                alt-value="ClearanceAccepted"
                                            >Yes, the Trouble Ticket is resolved</radio>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <radio
                                                v-model="clearanceForm.clearanceType"
                                                alt-value="ClearanceRejected"
                                            >No, the Trouble Ticket is not resolved</radio>
                                            <div
                                                v-if="clearanceForm.clearanceType === 'ClearanceRejected'"
                                            >
                                                <app-select
                                                    v-model="clearanceForm.rejectionCode"
                                                    :options="[
                                                        {
                                                            id: 'ACRJ1401',
                                                            label: 'Service not restored'
                                                        },
                                                        {
                                                            id: 'ACRJ1402',
                                                            label: 'Fault Reoccurred'
                                                        },
                                                        {
                                                            id: 'ACRJ1403',
                                                            label: 'More information required'
                                                        }
                                                    ]"
                                                    value-key="id"
                                                    label-key="label"
                                                    placeholder="Reason"
                                                />
                                                <app-textarea
                                                    v-model="clearanceForm.description"
                                                    placeholder="Note"
                                                />
                                            </div>
                                        </td>
                                    </tr>                                    
                                    <tr>
                                        <td>
                                            <app-button 
                                                class="btn-blue"
                                                :loading="clearanceFormIsLoading"
                                                @click="submitClearanceForm"
                                            >Submit</app-button>
                                        </td>
                                    </tr>
                                </table>
                            </td>                            
                        </tr>
                        <tr>
                            <td><strong>Description</strong></td>
                            <td>
                                <div class="whitespace-pre-wrap">{{ ticket.description }}</div>
                            </td>                            
                        </tr>                                            
                    </table>
                </div>
                <h2 class="font-thin tracking-widest uppercase mt-6 mb-3">Updates</h2>
                <tabulator
                    :data="ticket.updates"
                    :columns="[
                        {
                            title: 'Created',
                            field: 'created_at_for_human',
                            minWidth: 190,
                        },
                        {
                            title: 'Status',
                            field: 'update_data',
                            minWidth: 150,
                            formatter({_cell}) {
                                return `<div>${_cell.value.status}<br><span class='text-xs text-gray-600'>${_cell.value.notificationType}</span></div>`;
                            }
                        },
                        {
                            title: 'Comment',
                            field: 'update_data',
                            minWidth: 300,
                            formatter({_cell}) {
                                if (_cell.value.comment) {
                                    return `<div class='whitespace-pre-wrap'>${_cell.value.comment}</div>`;
                                }
                            }
                        }
                    ]"
                    :show-header="false"
                    :show-footer="false"
                />
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';

    export default {
        name: 'widebandClosedTickets',
        data() {
            return {
                showPreviewModal: false,
                tableIsLoading: false,
                tableLoadingText: null,
                clearanceFormIsLoading: false,
                ajaxParams: {type: 'closed'},
                refreshTable: false,
                q: null,
                clearanceForm: new Form({
                    clearanceType: 'ClearanceAccepted',
                    description: 'We accept your resolution',
                    employee: 'Access Seeker',
                    rejectionCode: null,
                }),
                columns: [
                    {
                        title: 'ID',
                        field: 'id',
                        sorter: 'number',
                        minWidth: 100,
                    },
                    {
                        title: 'Created',
                        field: 'created_at_for_human',
                        sorter: 'string',
                        minWidth: 190,
                    },
                    {
                        title: 'Last Updated',
                        field: 'updated_at_for_human',
                        sorter: 'string',
                        minWidth: 190,
                    },
                    {
                        title: 'Category',
                        field: 'category',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Last Notification',
                        field: 'last_notification_type',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Wideband Service ID',
                        field: 'product_id',
                        sorter: 'number',
                        minWidth: 200,
                    }
                ],
                actions: [
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                tickets: 'wideband/tickets/items',
                ticket: 'wideband/tickets/selected'
            })
        },
        methods: {
            preview(tickets) {
                if (tickets.length) {
                    this.showPreviewModal = true;
                    this.$store.commit('wideband/tickets/setSelected', tickets[0]);
                }
            },
            submitClearanceForm() {
                this.clearanceFormIsLoading = true;
                this.$store.dispatch('wideband/tickets/clearance', this.clearanceForm.payload())
                    .then(() => {
                        this.clearanceFormIsLoading = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Clearance response was successfully submitted.'
                        });
                    })
                    .catch(({ response }) => {
                        this.clearanceFormIsLoading = false;
                        this.clearanceForm.errors.record(response.data)
                    });
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                this.$store.dispatch('wideband/tickets/sync', 'closed')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.'
                        });
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            },
        }
    }
</script>